import { t } from '../i18n';
import { useEffect, useRef, useState } from 'react';

import type {
  CalcKind,
  CellValue,
  ChipColor,
  Property,
  PropertyType,
  RowBackground,
  SortRule,
  View,
} from '@noti/shared';

import {
  calcOptions,
  calcValue,
  CHIP_COLOR,
  columnWidths,
  gridTemplate,
  TYPE_LABEL,
  visibleProperties,
} from './database-rows';
import type { RowGroup } from './database-rows';
import { DatabaseCell } from './DatabaseCell';
import { ADDABLE, DatabaseColumnMenu } from './DatabaseColumnMenu';
import { DatabaseRowMenu } from './DatabaseRowMenu';
import type { RowAction } from './DatabaseRowMenu';
import { Dropdown } from './DatabaseDropdown';
import { blockBackgroundVar } from './extensions';

/**
 * The table view: header row, body rows, calculation footer and the add
 * row/column affordances. Measurements live in `dbcard__*` (workspace.css).
 */

export type { RowAction } from './DatabaseRowMenu';

export interface DatabaseTableProps {
  properties: Property[];
  view: View;
  groups: RowGroup[];
  people: { id: string; name: string }[];
  relationOptions?: Record<string, { id: string; title: string }[]>;
  showCalc: boolean;
  onWriteCell: (rowId: string, property: Property, value: CellValue) => void;
  onRowColor: (rowId: string, color: RowBackground | null) => void;
  onRowAction: (rowId: string, action: RowAction) => void;
  onOpenRow: (rowId: string) => void;
  onWidthChange: (propertyId: string, width: number) => void;
  onSort: (rule: Omit<SortRule, 'id'>) => void;
  onFilter: (propertyId: string) => void;
  onGroupBy: (propertyId: string) => void;
  onHide: (propertyId: string) => void;
  onColumnColor: (propertyId: string, color: RowBackground | null) => void;
  onColumnType: (propertyId: string, type: PropertyType) => void;
  onRenameColumn: (propertyId: string, name: string) => void;
  onOptionsText: (propertyId: string, text: string) => void;
  onClearColumn: (propertyId: string) => void;
  onDuplicateColumn: (propertyId: string) => void;
  onInsertColumn: (afterId: string | null) => void;
  onDeleteColumn: (propertyId: string) => void;
  onAddRow: () => void;
  onAddProperty: (name: string, type: PropertyType) => void;
  onCalcChange: (propertyId: string, kind: CalcKind) => void;
  onRenameOption: (propertyId: string, value: string, name: string) => void;
  onRecolorOption: (propertyId: string, value: string, color: ChipColor) => void;
  onRemoveOption: (propertyId: string, value: string) => void;
  onCreateOption: (propertyId: string, name: string) => void;
}

const WIDTH_SNAP = [100, 140, 180, 220, 280, 360, 480];
const MIN_COLUMN_WIDTH = 80;
const MAX_COLUMN_WIDTH = 640;

function snapWidth(width: number): number {
  const clamped = Math.max(MIN_COLUMN_WIDTH, Math.min(MAX_COLUMN_WIDTH, width));
  const hit = WIDTH_SNAP.find((step) => Math.abs(step - clamped) <= 10);
  return hit ?? Math.round(clamped);
}

function sortArrow(view: View, propertyId: string): string {
  const sort = view.sorts.find((rule) => rule.propId === propertyId);
  if (!sort) return '';
  return sort.dir === 'desc' ? '↓' : '↑';
}

function calcLabel(type: PropertyType, kind: CalcKind): string {
  return calcOptions(type).find(([option]) => option === kind)?.[1] ?? '';
}

export function DatabaseTable(props: DatabaseTableProps): React.ReactElement {
  const { properties, view, groups } = props;
  const columns = visibleProperties(properties, view);
  const widths = columnWidths(columns, view);
  // While dragging, the column follows the pointer: no round-trip per move.
  const [openColumnId, setOpenColumnId] = useState<string | null>(null);
  const [openRowId, setOpenRowId] = useState<string | null>(null);
  const [dragging, setDragging] = useState<{ id: string; width: number } | null>(null);
  const live = widths.map((width, index) =>
    dragging && columns[index]?.id === dragging.id ? dragging.width : width,
  );
  const template = gridTemplate(live);

  const rows = groups.flatMap((group) => group.rows);
  const lastRowId = rows[rows.length - 1]?.id ?? null;

  return (
    <div className="dbcard__tblx">
      <div className="dbcard__table" style={{ minWidth: 'max-content' }}>
        <div className="dbh" style={{ gridTemplateColumns: template }}>
          {columns.map((property, index) => (
            <div
              key={property.id}
              className={`dbh__col${index === columns.length - 1 ? ' hl' : ''}`}
              style={
                property.color
                  ? { background: blockBackgroundVar(property.color) ?? undefined }
                  : undefined
              }
            >
              <DatabaseColumnMenu
                property={property}
                properties={properties}
                sortMark={sortArrow(view, property.id)}
                open={openColumnId === property.id}
                onOpenChange={(next) => {
                  if (next) setOpenRowId(null);
                  setOpenColumnId((current) =>
                    next ? property.id : current === property.id ? null : current,
                  );
                }}
                table={props}
              />
              <ResizeHandle
                width={live[index] ?? 140}
                onDrag={(width) => setDragging({ id: property.id, width })}
                onCommit={(width) => {
                  setDragging(null);
                  props.onWidthChange(property.id, width);
                }}
              />
            </div>
          ))}
        </div>

        {groups.map((group) => (
          <div className="dbcard__groupbody" key={group.name || 'all'}>
            {group.name ? (
              <div className="dbcard__group">
                <span
                  className="chip"
                  style={{
                    background: CHIP_COLOR[group.chip].bg,
                    color: CHIP_COLOR[group.chip].fg,
                  }}
                >
                  {group.name}
                </span>
                <span className="dbcard__groupcount">{group.rows.length}</span>
              </div>
            ) : null}
            {group.rows.map((row) => (
              <div
                key={row.id}
                className={`crow${row.id === lastRowId ? ' lastrow' : ''}`}
                style={{
                  gridTemplateColumns: template,
                  background: row.color ? (blockBackgroundVar(row.color) ?? undefined) : undefined,
                }}
              >
                <DatabaseRowMenu
                  row={row}
                  open={openRowId === row.id}
                  onOpenChange={(next) => {
                    if (next) setOpenColumnId(null);
                    setOpenRowId((current) =>
                      next ? row.id : current === row.id ? null : current,
                    );
                  }}
                  onAction={props.onRowAction}
                  onColor={props.onRowColor}
                  onOpen={props.onOpenRow}
                />
                {columns.map((property, index) => (
                  <div
                    key={property.id}
                    className={`nmc${index === columns.length - 1 ? ' lastcol' : ''}`}
                    style={
                      property.color
                        ? { background: blockBackgroundVar(property.color) ?? undefined }
                        : undefined
                    }
                  >
                    <DatabaseCell
                      row={row}
                      property={property}
                      people={props.people}
                      relationOptions={props.relationOptions}
                      onOpenRow={props.onOpenRow}
                      onWrite={props.onWriteCell}
                      onCreateOption={(target, name) => props.onCreateOption(target.id, name)}
                      onRenameOption={(target, value, name) =>
                        props.onRenameOption(target.id, value, name)
                      }
                      onRecolorOption={(target, value, color) =>
                        props.onRecolorOption(target.id, value, color)
                      }
                      onRemoveOption={(target, value) => props.onRemoveOption(target.id, value)}
                    />
                    {index === 0 ? (
                      <button
                        type="button"
                        className="hov3 dbcard__open"
                        aria-label={t('database.openRow')}
                        title={t('database.open')}
                        onClick={() => props.onOpenRow(row.id)}
                      >
                        {t('database.open')}
                      </button>
                    ) : null}
                  </div>
                ))}
                <span />
              </div>
            ))}
          </div>
        ))}

        {props.showCalc ? (
          <div className="crow dbcard__calc" style={{ gridTemplateColumns: template }}>
            <span />
            {columns.map((property) => {
              const kind = view.calc[property.id] ?? 'none';
              const label =
                kind === 'none'
                  ? 'Calculate'
                  : `${calcLabel(property.type, kind)} ${calcValue(rows, property, kind)}`;
              return (
                <div className="dbcard__calcwrap" key={property.id}>
                  <Dropdown
                    className="dbcard__calcdd"
                    label={t('database.calculateFor', { name: property.name })}
                    value={kind}
                    noChevron
                    options={calcOptions(property.type).map(([value, text]) => ({
                      value,
                      label: text,
                    }))}
                    onPick={(value) => props.onCalcChange(property.id, value as CalcKind)}
                  >
                    <span className="dbcard__calclabel">{label}</span>
                  </Dropdown>
                </div>
              );
            })}
            <span />
          </div>
        ) : null}
      </div>

      <AddRowButton onAdd={props.onAddRow} lastRowId={lastRowId} />
      <AddColumnButton onAdd={props.onAddProperty} />
    </div>
  );
}

function AddRowButton({
  onAdd,
  lastRowId,
}: {
  onAdd: () => void;
  lastRowId: string | null;
}): React.ReactElement {
  return (
    <div className={`addr${lastRowId ? ' addr--ready' : ''}`}>
      <button
        type="button"
        className="pl"
        aria-label={t('database.newRow')}
        title={t('database.newRow')}
        onClick={onAdd}
      >
        +
      </button>
    </div>
  );
}

function AddColumnButton({
  onAdd,
}: {
  onAdd: (name: string, type: PropertyType) => void;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [type, setType] = useState<PropertyType>('text');

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [open]);

  return (
    <div className="addc">
      <button
        type="button"
        className="pl"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('database.newPropertyName')}
        title={t('database.newPropertyName')}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((current) => !current);
        }}
      >
        +
      </button>
      {open ? (
        <div
          className="dbcard__addprop"
          role="menu"
          aria-label={t('database.propertyName')}
          onClick={(event) => event.stopPropagation()}
        >
          <span className="dbcard__eyebrow">{t('database.newPropertyEyebrow')}</span>
          <input
            className="ni dbcard__addinput"
            value={name}
            placeholder={t('database.propertyName')}
            aria-label={t('database.propertyName')}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && name.trim()) {
                onAdd(name.trim(), type);
                setName('');
                setOpen(false);
              }
            }}
          />
          <Dropdown
            label="Type"
            value={type}
            options={ADDABLE.map((option) => ({ value: option, label: TYPE_LABEL[option] }))}
            onPick={(value) => setType(value as PropertyType)}
          />
          <button
            type="button"
            className="prim dbcard__addbtn"
            onClick={() => {
              if (!name.trim()) return;
              onAdd(name.trim(), type);
              setName('');
              setOpen(false);
            }}
          >
            {t('database.add')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Column resize. The move/up listeners go on the window inside the pointerdown
 * handler itself: binding them from an effect means waiting for a re-render
 * first, which is what makes a drag feel laggy.
 */
function ResizeHandle({
  width,
  onDrag,
  onCommit,
}: {
  width: number;
  onDrag: (width: number) => void;
  onCommit: (width: number) => void;
}): React.ReactElement {
  const start = useRef<number | null>(null);

  const drag = (event: React.PointerEvent<HTMLDivElement>): void => {
    event.preventDefault();
    start.current = event.clientX;
    let latest = width;
    const move = (moveEvent: PointerEvent) => {
      const origin = start.current;
      if (origin === null) return;
      latest = Math.max(
        MIN_COLUMN_WIDTH,
        Math.min(MAX_COLUMN_WIDTH, width + (moveEvent.clientX - origin)),
      );
      onDrag(latest);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      start.current = null;
      onCommit(snapWidth(latest));
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  return (
    <div className="dbh__resize" title={t('database.dragToResize')} onPointerDown={drag}>
      <span />
    </div>
  );
}
